import { ColumnsType } from 'antd/lib/table';
import type { jsPDF as JsPDFType } from 'jspdf';
import type { UserOptions } from 'jspdf-autotable';
import {
  getReportExportTableData,
  ReportExportMetric,
  toReportExportCellValue,
} from './report-export-table-data';

interface ExportReportToPdfParams<T> {
  chartsElement: HTMLElement | null;
  columns: ColumnsType<T>;
  fileName: string;
  metrics: ReportExportMetric[];
  reportTitle: string;
  rows: T[];
}

type JsPdfWithAutoTable = JsPDFType & {
  autoTable: (options: UserOptions) => JsPdfWithAutoTable;
  lastAutoTable: { finalY: number };
};

const loadImageSize = (
  dataUrl: string,
): Promise<{ height: number; width: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = (): void =>
      resolve({ height: image.naturalHeight, width: image.naturalWidth });
    image.onerror = reject;
    image.src = dataUrl;
  });

export const exportReportToPdf = async <T>({
  chartsElement,
  columns,
  fileName,
  metrics,
  reportTitle,
  rows,
}: ExportReportToPdfParams<T>): Promise<void> => {
  const [{ default: JsPDF }, { toPng }] = await Promise.all([
    import('jspdf'),
    import('html-to-image'),
  ]);

  await import('jspdf-autotable');

  const doc = new JsPDF({
    orientation: 'landscape',
  }) as JsPdfWithAutoTable;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  let cursorY = margin;

  doc.setFontSize(16);
  doc.text(reportTitle, margin, cursorY);
  cursorY += 6;

  doc.setFontSize(9);
  doc.text(`Generated ${new Date().toLocaleString()}`, margin, cursorY);
  cursorY += 6;

  const metricsBody = metrics.map(
    ({ caption, label, value }: ReportExportMetric) => [
      label,
      toReportExportCellValue(value),
      caption ?? '',
    ],
  );

  doc.autoTable({
    body: metricsBody,
    head: [['Label', 'Value', 'Caption']],
    margin: { left: margin, right: margin },
    startY: cursorY,
    styles: { fontSize: 8 },
    theme: 'grid',
  });
  cursorY = doc.lastAutoTable.finalY + 8;

  if (chartsElement) {
    try {
      const chartsImage = await toPng(chartsElement, { pixelRatio: 2 });
      const { height, width } = await loadImageSize(chartsImage);
      const imageWidth = contentWidth;
      const imageHeight = (height / width) * imageWidth;

      if (cursorY + imageHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }

      doc.addImage(
        chartsImage,
        'PNG',
        margin,
        cursorY,
        imageWidth,
        imageHeight,
      );
      cursorY += imageHeight + 8;
    } catch (error) {
      console.warn('Failed to capture report charts for PDF export', error);
    }
  }

  const { header, rows: tableRows } = getReportExportTableData<T>(
    columns,
    rows,
  );

  doc.autoTable({
    body: tableRows,
    head: [header],
    margin: { left: margin, right: margin },
    startY: cursorY,
    styles: { fontSize: 7 },
    theme: 'striped',
  });

  doc.save(fileName);
};
